import { Component, computed, inject, viewChild } from '@angular/core';
import { FolderServiceMailRecordsStore } from './folder-service-mail-records.store';
import { TableComponent } from 'packages/client/src/app/modules/shared/components/kit/table/table.component';
import {
    dateField,
    dateTimeField,
    numberField,
    textColumn,
} from 'packages/client/src/app/modules/shared/components/kit/table/table.fields';
import { translate } from '../../../../../shared/shared.global-service';
import { BaseDialogService } from 'packages/client/src/app/components/kit/base-dialog/base-dialog-service';
import {
    MailRecordDialogComponent,
    ServiceMailDialogData,
} from '../../../../components/services/dialogs/mail-record-dialog/mail-record-dialog.component';
import { ButtonComponent } from 'packages/client/src/app/components/kit/button/button.component';
import { ServiceMailsObject } from '@libs/types/src/services/object-type/service/service-mails.object';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import { checkOk } from '../../../../content.error';
import { getMailsFrag } from './folder-service-mail-records.query';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { UserChipComponent } from '../../../../../../components/kit/user-chip/user-chip.component';
import { UserGridItemObject } from '@it2go/types';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { formatPerson } from '../../../../../shared/pipes/format-person.pipe';
import { TableColumn } from '../../../../../shared/components/kit/table/table.types';

@Component({
    selector: 'app-folder-service-mail-records',
    templateUrl: './folder-service-mail-records.component.html',
    styleUrl: './folder-service-mail-records.component.scss',
    providers: [FolderServiceMailRecordsStore],
    imports: [
        TableComponent,
        ButtonComponent,
        PrimeTemplate,
        UserChipComponent,
    ],
})
export class FolderServiceMailRecordsComponent {

    public readonly dialogService = inject(BaseDialogService);
    public readonly confirmBuilder = inject(ConfirmBuilder);
    public readonly store = inject(FolderServiceMailRecordsStore);

    protected readonly table = viewChild(TableComponent);


    protected columns = computed(() => {
        return <TableColumn[]>[
            numberField('mailNumber'),
            {
                name: 'direction',
                data: (direction: string) => translate.instant(`service.mailRecords.direction.${direction}`),
                filterable: {
                    type: 'select',
                    selectOptions: () => [
                        {
                            name: translate.instant('service.mailRecords.direction.incoming'),
                            value: 'incoming',
                        },
                        {
                            name: translate.instant('service.mailRecords.direction.outgoing'),
                            value: 'outgoing',
                        },
                    ],
                },
            },
            textColumn('info'),
            {
                name: 'type',
                data: (item: any) => {
                    return translate.instant(`enum.serviceMails.type.${item}`);
                },
            },
            {
                ...textColumn('sender'),
                sortable: false,
            },
            {
                ...textColumn('recipient'),
                sortable: false,
            },
            dateTimeField('sendDate'),
            dateTimeField('receiveDate'),
            dateField('deadline'),
            {
                name: 'users',
                exportAs: (users: UserGridItemObject[]) =>
                    users
                        .map((user) => formatPerson(user))
                        .join(', ')
                ,
            },
            {
                name: 'state',
                data: (item: any) => {
                    return translate.instant(`enum.serviceMails.state.${item}`);
                },
            },
            {
                ...textColumn('note'),
                sortable: false,
            },
        ];
    });

    protected additionalFilters = computed<any>(() => {
        const filter = new FilterBuilder();

        const folderId = this.store.folderStore.folderId();

        if (folderId) {
            filter.folderId(folderId);
        }

        return filter.filter()?.filter || [];
    });

    public refreshTable() {
        this.table()?.reload();
    }

    protected editServiceMailRecord(mailRecord: ServiceMailsObject) {
        if (!this.store.isAllowed('folderMail.update')()) return;
        const data: ServiceMailDialogData = {
            direction: mailRecord.direction,
            mailRecordData: mailRecord,
        };

        this.serviceMailRecordDialog(data);
    }

    protected createServiceMailRecord(direction: string) {
        if (!this.store.isAllowed('folderMail.create')()) return;
        const data: ServiceMailDialogData = {
            folderId: this.store.folderStore.folderId(),
            serviceId: this.store.folderStore.folder()?.serviceId,
            direction,
        };

        this.serviceMailRecordDialog(data);
    }

    protected serviceMailRecordDialog(data: ServiceMailDialogData) {
        const dialog = this.dialogService.open(
            MailRecordDialogComponent,
            {
                header: translate.instant(`service.mailRecords.direction.${data.direction}`),
                styleClass: 'p-dialog-width-60',
                data,
            },
        );

        dialog?.onClose.subscribe((it) => {
            if (it === true) {
                this.refreshTable();
            }
        });
    }

    public async deleteServiceMailRecord(event: any): Promise<void> {
        if (!this.store.isAllowed('folderMail.delete')()) return;
        this.confirmBuilder.confirmDelete(
            async () => {
                const res = await this.store.api.deleteServiceMails({ id: event.item.id });
                checkOk(res, 'delete');

                this.refreshTable();
            },
            event,
        );
    }

    protected readonly getMailsFrag = getMailsFrag;
}
